import { ResponseNotOkError, Session } from "@paywithglide/glide-js";
import ModalBackButton from "./../components/ModalBackButton";
import TransactionCheckoutHeader from "./../components/TransactionCheckoutHeader";
import PrimaryButton from "./../components/PrimaryButton";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CircleCheckBig,
  MousePointerClick,
  LoaderCircle,
  X,
  AlertTriangle,
} from "lucide-react";
import Alert from "./../components/Alert";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { logger } from "../lib/logger";
import { checkKycStatus } from "../lib/onramp/checkKycStatus";
import { glideLocalStorage } from "../lib/glideLocalStorage";
import { createTransaction, UpiNote } from "../lib/onramp/createTransaction";
import {
  checkTransactionStatus,
  isTransactionComplete,
  isTransactionFailed,
  getTransactionErrorMessage,
} from "../lib/onramp/checkTransactionStatus";
import { getQuote, OnrampQuoteResponse } from "../lib/onramp/getQuote";
import QRCode from "../components/QRCode";

// Helper to extract clean error message from API errors
function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ResponseNotOkError) {
    try {
      const parsed = JSON.parse(err.response);
      if (parsed.error && typeof parsed.error === "string") {
        return parsed.error;
      }
    } catch {
      // Response is not JSON, use as-is if short enough
      if (err.response.length < 100) {
        return err.response;
      }
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}
import {
  createOnrampSession,
  getKycStatusCategory,
} from "../lib/onramp/createOnrampSession";

type Step =
  | "initializing"
  | "no_phone_error"
  | "quote"
  | "creating_session"
  | "kyc_iframe"
  | "checking_kyc_status"
  | "kyc_error"
  | "kyc_in_review"
  | "kyc_temporary_failure"
  | "kyc_permanent_failure"
  | "creating_transaction"
  | "show_qr_code"
  | "polling_payment"
  | "transaction_error"
  | "payment_success";

type Props = {
  session: Session;
  onBack: () => void;
  onSuccess: (session: Session) => void;
};

export default function PayWithOnramp({ session, onBack, onSuccess }: Props) {
  const { glideConfig, ...depositConfig } = useGlideDepositConfig();

  useEffect(() => {
    return () => {
      clearTimeout(pollingIntervalRef.current);
      clearInterval(kycPopupCheckIntervalRef.current);
      if (kycPopupRef.current && !kycPopupRef.current.closed) {
        kycPopupRef.current.close();
      }
    };
  }, []);

  const [step, setStep] = useState<Step>("initializing");
  const [kycUrl, setKycUrl] = useState<string>("");
  const [customerId, setCustomerId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Quote state
  const [quote, setQuote] = useState<OnrampQuoteResponse | null>(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);

  const [, setTransactionId] = useState<string>("");
  const [upiDetails, setUpiDetails] = useState<{
    vpa: string;
    name: string;
    amount: string;
    otp: string;
  }>();
  const [upiLink, setUpiLink] = useState<string>("");
  const [upiNotes, setUpiNotes] = useState<UpiNote[]>([]);
  const [cryptoAmount, setCryptoAmount] = useState<string>("");

  const [isOtpCopied, setIsOtpCopied] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const [showQrFallbackButton, setShowQrFallbackButton] = useState(false);
  const [hasOpenedUpiApp, setHasOpenedUpiApp] = useState(false);
  const [hasOpenedKycPopup, setHasOpenedKycPopup] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout>();
  const kycPopupRef = useRef<Window | null>(null);
  const kycPopupCheckIntervalRef = useRef<NodeJS.Timeout>();

  // Detect if user is on mobile device
  const isMobile =
    typeof navigator !== "undefined" &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  const fetchQuote = useCallback(async () => {
    setIsLoadingQuote(true);
    try {
      const quoteData = await getQuote(glideConfig, {
        cryptoAmount: session.sponsoredTransactionAmount,
        paymentMethodType: "UPI",
      });
      setQuote(quoteData);
    } catch (err) {
      logger.error(`Failed to fetch quote: ${err}`);
      setError(
        extractErrorMessage(err, "Unable to get quote. Please try again."),
      );
    } finally {
      setIsLoadingQuote(false);
    }
  }, [glideConfig, session.sponsoredTransactionAmount]);

  // Initialize: show quote screen first
  useEffect(() => {
    const initializeQuote = async () => {
      // Security check: userPhoneNumber must come from widget session (not URL)
      if (!depositConfig.userPhoneNumber) {
        setError(
          "Phone number not provided. Onramp requires the client to provide userPhoneNumber when creating the widget session.",
        );
        setStep("no_phone_error");
        return;
      }

      // Check if we have widget session ID
      if (!depositConfig.widgetSessionId) {
        setError(
          "Widget session not found. Onramp requires a valid widget session with userPhoneNumber.",
        );
        setStep("no_phone_error");
        return;
      }

      try {
        // First, show the quote to the user
        await fetchQuote();
        setStep("quote");
      } catch (err) {
        logger.error(`Failed to initialize onramp: ${err}`);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to get quote. Please try again.",
        );
        setStep("no_phone_error");
      }
    };

    initializeQuote();
  }, [
    depositConfig.userPhoneNumber,
    depositConfig.widgetSessionId,
    glideConfig,
    fetchQuote,
  ]);

  // Auto-open UPI app on mobile when QR step is shown
  useEffect(() => {
    if (
      (step === "show_qr_code" || step === "polling_payment") &&
      isMobile &&
      upiLink &&
      !hasOpenedUpiApp
    ) {
      // Open UPI deep link immediately
      setHasOpenedUpiApp(true);
      window.location.href = upiLink;

      // Show QR fallback button after 3 seconds
      const timer = setTimeout(() => {
        setShowQrFallbackButton(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [step, isMobile, upiLink, hasOpenedUpiApp]);

  // Function to open KYC popup
  const openKycPopup = useCallback(() => {
    if (!kycUrl) return;

    // Mark that we've opened the popup (to show waiting state)
    setHasOpenedKycPopup(true);

    // Clear any existing interval
    clearInterval(kycPopupCheckIntervalRef.current);

    const width = 500;
    const height = 700;
    const top = Math.max((window.innerHeight - height) / 2, 0);
    const left = Math.max((window.innerWidth - width) / 2, 0);

    const popup = window.open(
      kycUrl,
      "onramp_kyc",
      `popup=true,width=${width},height=${height},left=${left},top=${top}`,
    );

    if (popup) {
      kycPopupRef.current = popup;

      // Poll to check if popup redirected to success URL or was closed
      kycPopupCheckIntervalRef.current = setInterval(() => {
        try {
          // Check if popup was closed by user
          if (popup.closed) {
            clearInterval(kycPopupCheckIntervalRef.current);
            return;
          }

          // Check if popup redirected to success URL
          const popupUrl = popup.location.href;
          if (popupUrl.startsWith("https://paywithglide.xyz")) {
            clearInterval(kycPopupCheckIntervalRef.current);
            popup.close();
            kycPopupRef.current = null;
          }
        } catch {
          // Cross-origin error - popup is still on onramp domain, continue polling
        }
      }, 500);
    }
  }, [kycUrl]);

  const handleKycComplete = async () => {
    // User says they completed KYC, now poll for status
    setStep("checking_kyc_status");
    setError(null);

    try {
      // Simulate loading for 5 seconds before checking
      await new Promise((resolve) => setTimeout(resolve, 5000));

      // Now check KYC status
      const response = await checkKycStatus(glideConfig, {
        customerId,
      });

      const kycCategory = getKycStatusCategory(response.data.status);

      // Handle based on category
      switch (kycCategory) {
        case "passed":
          // KYC completed, proceed to create transaction
          await onContinue(customerId);
          return;
        case "in_review":
          // KYC is under review
          setStep("kyc_in_review");
          return;
        case "temporary_failure":
          // Temporary failure, user can retry
          setError(
            "Your verification was not successful. Please try again with clearer documents.",
          );
          setStep("kyc_temporary_failure");
          return;
        case "permanent_failure":
          // Permanent failure, user must contact support
          setError(
            "Your verification has been permanently rejected. Please contact support for assistance.",
          );
          setStep("kyc_permanent_failure");
          return;
        case "needs_login":
        default:
          // User still needs to complete login/verification, go back to KYC iframe
          setError(
            "Verification not complete. Please complete the verification process.",
          );
          setStep("kyc_error");
          return;
      }
    } catch (err) {
      logger.error(`Failed to verify KYC status: ${err}`);
      setError(
        err instanceof Error
          ? err.message
          : "Unable to verify KYC completion. Please try again.",
      );
      setStep("kyc_error");
    }
  };

  const handleBack = () => {
    if (step === "kyc_iframe" || step === "checking_kyc_status") {
      // Go back to quote screen from KYC
      // Reset customerId so KYC is re-checked when user presses Continue again
      setCustomerId("");
      setStep("quote");
      setError(null);
    } else if (step === "kyc_error" || step === "kyc_temporary_failure") {
      // Go back to quote for retry
      // Reset customerId so KYC is re-checked when user presses Continue again
      setCustomerId("");
      setStep("quote");
      setError(null);
    } else if (step === "kyc_in_review" || step === "kyc_permanent_failure") {
      // Go back to previous payment method selection
      onBack();
    } else if (step === "quote") {
      onBack();
    } else if (step === "creating_transaction" || step === "creating_session") {
      setStep("quote");
      setError(null);
    } else if (step === "show_qr_code" || step === "polling_payment") {
      // Stop polling
      clearTimeout(pollingIntervalRef.current);
      setStep("quote");
      setError(null);
    } else if (step === "transaction_error") {
      // Reset customerId so KYC is re-checked when user tries again
      setCustomerId("");
      setStep("quote");
      setError(null);
    } else {
      onBack();
    }
  };

  /**
   * Generate a UPI deep link for QR code
   */
  const generateUpiLink = (
    vpa: string,
    name: string,
    amount: string,
    note: string,
  ) => {
    return `upi://pay?pa=${encodeURIComponent(vpa)}&pn=${encodeURIComponent(name)}&am=${encodeURIComponent(amount)}&tn=${encodeURIComponent(note)}`;
  };

  const onContinue = async (customerIdOverride?: string) => {
    setError(null);

    // Use override if provided (for direct calls after KYC), otherwise use state
    let effectiveCustomerId = customerIdOverride || customerId;

    // If we don't have a customerId yet, we need to create the onramp session first
    if (!effectiveCustomerId) {
      setStep("creating_session");
      try {
        // Call the secure endpoint that uses phone from widget session
        const sessionData = await createOnrampSession(glideConfig, {
          sessionId: depositConfig.widgetSessionId!,
          email: depositConfig.payerEmail,
        });

        setCustomerId(sessionData.customerId);
        setKycUrl(sessionData.kycUrl);
        effectiveCustomerId = sessionData.customerId;

        // Store customerId for transaction polling
        await glideLocalStorage.setItem(
          "onramp.customerId",
          sessionData.customerId,
        );

        // Handle KYC status based on category
        const kycCategory = getKycStatusCategory(sessionData.kycStatus);

        switch (kycCategory) {
          case "passed":
            // KYC completed, proceed to create transaction
            break;
          case "needs_login":
            // User needs to complete login/OTP, show KYC iframe
            setStep("kyc_iframe");
            return;
          case "in_review":
            // KYC is under review, show info screen
            setStep("kyc_in_review");
            return;
          case "temporary_failure":
            // Temporary failure, user can retry
            setError(
              "Your verification was not successful. Please try again with clearer documents.",
            );
            setStep("kyc_temporary_failure");
            return;
          case "permanent_failure":
            // Permanent failure, user must contact support
            setError(
              "Your verification has been permanently rejected. Please contact support for assistance.",
            );
            setStep("kyc_permanent_failure");
            return;
          default:
            // Unknown status, show KYC iframe to be safe
            setStep("kyc_iframe");
            return;
        }
      } catch (err) {
        logger.error(`Failed to create onramp session: ${err}`);
        setError(
          extractErrorMessage(
            err,
            "Failed to initialize payment session. Please try again.",
          ),
        );
        setStep("transaction_error");
        return;
      }
    }

    // Create OnRamp transaction
    setStep("creating_transaction");

    try {
      const depositAddress =
        depositConfig.recipient ||
        depositConfig.connectedWalletAddress ||
        session.depositAddress;

      if (!depositAddress) {
        throw new Error(
          "Deposit address not found. Please provide a recipient address.",
        );
      }

      // Get the crypto amount from session
      const txCryptoAmount = session.sponsoredTransactionAmount;

      const transactionData = await createTransaction(glideConfig, {
        customerId: effectiveCustomerId,
        cryptoAmount: txCryptoAmount,
        paymentMethodType: "UPI",
        depositAddress,
      });

      setTransactionId(transactionData.transactionId);
      setCryptoAmount(transactionData.toAmount);

      // Extract UPI details - handle sandbox mode where upi object may be missing
      const upi = transactionData.fiatPaymentInstructions?.upi;
      const otp =
        transactionData.fiatPaymentInstructions?.otp ||
        transactionData.transactionId;
      const amount = transactionData.fromAmount;

      // Use provided UPI details or fallback for sandbox
      const vpa = upi?.vpa || "sandbox@upi";
      const name = upi?.name || "Sandbox Test";

      setUpiDetails({
        vpa,
        name,
        amount,
        otp,
      });

      // Store UPI notes for display
      if (transactionData.fiatPaymentInstructions?.upiNotes) {
        setUpiNotes(transactionData.fiatPaymentInstructions.upiNotes);
      }

      // Generate UPI deep link for QR code
      const link = generateUpiLink(vpa, name, amount, otp);
      setUpiLink(link);

      setStep("show_qr_code");

      // Start polling after a short delay
      setTimeout(() => {
        startTransactionPolling(
          transactionData.transactionId,
          effectiveCustomerId,
        );
      }, 3000);
    } catch (err) {
      logger.error(`Failed to create OnRamp transaction: ${err}`);
      setError(
        extractErrorMessage(
          err,
          "Unable to create transaction. Please try again.",
        ),
      );
      setStep("transaction_error");
    }
  };

  const startTransactionPolling = async (
    txId: string,
    customerIdToUse: string,
  ) => {
    setStep("polling_payment");

    const maxAttempts = 60; // 60 attempts at 5s = 5 minutes
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setError(
          "Payment timeout. Please check your payment status or try again.",
        );
        setStep("transaction_error");
        return;
      }

      attempts++;

      try {
        const statusData = await checkTransactionStatus(glideConfig, {
          transactionId: txId,
          customerId: customerIdToUse,
        });

        if (isTransactionComplete(statusData.status)) {
          // Payment successful!
          setStep("payment_success");
          // Call the original onSuccess to complete the flow
          onSuccess(session);
          return;
        }

        if (isTransactionFailed(statusData.status)) {
          // Payment failed
          setError(getTransactionErrorMessage(statusData.status));
          setStep("transaction_error");
          return;
        }

        // Still in progress, poll again
        pollingIntervalRef.current = setTimeout(() => poll(), 5000);
      } catch (err) {
        logger.error(
          `Transaction status check failed (attempt ${attempts}): ${err}`,
        );

        // Retry on error
        if (attempts < maxAttempts) {
          pollingIntervalRef.current = setTimeout(() => poll(), 5000);
        } else {
          setError("Unable to check payment status. Please try again.");
          setStep("transaction_error");
        }
      }
    };

    poll();
  };

  const copyOtpToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsOtpCopied(true);
      setTimeout(() => setIsOtpCopied(false), 3000);
    } catch (err) {
      logger.error(`Failed to copy OTP to clipboard: ${err}`);
    }
  };

  // Initializing step - loading state on initial widget load (no back button)
  if (step === "initializing") {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoaderCircle className="h-12 w-12 text-blue-500 animate-spin" />
        <p className="mt-4 text-lg font-medium text-[--color-text-primary]">
          Setting up payment...
        </p>
        <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
          Please wait while we prepare your payment session.
        </p>
      </div>
    );
  }

  // Creating session step - loading state after clicking Continue to Payment (has back button)
  if (step === "creating_session") {
    return (
      <>
        <ModalBackButton onClick={handleBack} />
        <div className="flex flex-col items-center justify-center py-12">
          <LoaderCircle className="h-12 w-12 text-blue-500 animate-spin" />
          <p className="mt-4 text-lg font-medium text-[--color-text-primary]">
            Setting up payment...
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            Please wait while we prepare your payment session.
          </p>
        </div>
      </>
    );
  }

  // No phone error - shown when userPhoneNumber is not provided in widget session
  if (step === "no_phone_error") {
    return (
      <>
        <ModalBackButton onClick={onBack} />
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <p className="mt-4 text-lg font-medium text-[--color-text-primary] text-center">
            Onramp Unavailable
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            {error ||
              "This payment method requires phone verification to be set up by the application."}
          </p>
          <p className="mt-4 text-xs text-[--color-text-secondary] text-center max-w-sm italic">
            If you're the developer, ensure you pass userPhoneNumber when
            creating the widget session.
          </p>
        </div>
      </>
    );
  }

  // Quote screen - shows INR amount before proceeding
  if (step === "quote") {
    if (isLoadingQuote) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <LoaderCircle className="h-12 w-12 text-blue-500 animate-spin" />
          <p className="mt-4 text-lg font-medium text-[--color-text-primary]">
            Getting quote...
          </p>
        </div>
      );
    }

    if (error || !quote) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <X className="h-8 w-8 text-red-600" />
          </div>
          <p className="mt-4 text-lg font-medium text-[--color-text-primary] text-center">
            Unable to get quote
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            {error || "Please try again."}
          </p>
          <PrimaryButton
            className="mt-4 w-full"
            onClick={() => window.location.reload()}
          >
            Try Again
          </PrimaryButton>
        </div>
      );
    }

    return (
      <div>
        <TransactionCheckoutHeader
          title="Payment Amount"
          subtitle="Review the amount you'll pay via UPI"
          centeredSubtitle
        />

        <div className="mt-4 flex flex-col items-center">
          <div className="bg-[--color-background-secondary] rounded-2xl px-8 py-6 w-full">
            <p className="text-sm text-[--color-text-secondary] text-center mb-2">
              You'll pay
            </p>
            <p className="text-4xl font-semibold text-[--color-text-primary] text-center">
              ₹{Number(quote.fromAmount).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <Alert
            id="onramp_quote_info"
            icon={<MousePointerClick strokeWidth={2.25} className="w-4 h-4" />}
            title="Pay with any UPI app"
            subtitle="Complete payment using Google Pay, PhonePe, Paytm, or any other UPI app."
          />
        </div>

        <PrimaryButton className="mt-4" onClick={() => onContinue()}>
          Continue to Payment
        </PrimaryButton>
      </div>
    );
  }

  // Checking KYC status step
  if (step === "checking_kyc_status") {
    return (
      <>
        <ModalBackButton onClick={handleBack} />
        <div className="flex flex-col items-center justify-center py-12">
          <LoaderCircle className="h-12 w-12 text-blue-500 animate-spin" />
          <p className="mt-4 text-lg font-medium text-[--color-text-primary]">
            Verifying your information...
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            Please wait while we confirm your verification is complete. This
            usually takes a few moments.
          </p>
        </div>
      </>
    );
  }

  // KYC error step
  if (step === "kyc_error") {
    return (
      <>
        <ModalBackButton onClick={handleBack} />
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <X className="h-8 w-8 text-red-600" />
          </div>
          <p className="mt-4 text-lg font-medium text-[--color-text-primary] text-center">
            Verification Error
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            {error ||
              "We couldn't verify your KYC completion. Please try again."}
          </p>

          <div className="mt-4 w-full space-y-2">
            <PrimaryButton onClick={() => handleKycComplete()}>
              Try Again
            </PrimaryButton>
            <button
              onClick={() => setStep("kyc_iframe")}
              className="w-full px-4 py-2 text-sm font-medium text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors"
            >
              Open Verification Again
            </button>
          </div>
        </div>
      </>
    );
  }

  // KYC in review - show info to check back later
  if (step === "kyc_in_review") {
    return (
      <>
        <ModalBackButton onClick={handleBack} />
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
            <LoaderCircle className="h-8 w-8 text-blue-600" />
          </div>
          <p className="mt-4 text-lg font-medium text-[--color-text-primary] text-center">
            Verification Under Review
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            Your identity verification is currently being reviewed. This usually
            takes a few minutes but may take up to 24 hours.
          </p>
          <p className="mt-4 text-sm text-[--color-text-secondary] text-center max-w-sm">
            Please check back later to complete your payment.
          </p>

          <div className="mt-6 w-full">
            <PrimaryButton onClick={onBack}>
              Choose Another Payment Method
            </PrimaryButton>
          </div>
        </div>
      </>
    );
  }

  // KYC temporary failure - user can retry
  if (step === "kyc_temporary_failure") {
    return (
      <>
        <ModalBackButton onClick={handleBack} />
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <p className="mt-4 text-lg font-medium text-[--color-text-primary] text-center">
            Verification Failed
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            {error ||
              "Your verification was not successful. This may be due to unclear images or incorrect documents."}
          </p>
          <p className="mt-4 text-sm text-[--color-text-secondary] text-center max-w-sm">
            Please try again with clearer photos and make sure your documents
            are valid and readable.
          </p>

          <div className="mt-6 w-full space-y-2">
            <PrimaryButton onClick={() => onContinue()}>
              Try Again
            </PrimaryButton>
            <button
              onClick={onBack}
              className="w-full px-4 py-2 text-sm font-medium text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors"
            >
              Choose Another Payment Method
            </button>
          </div>
        </div>
      </>
    );
  }

  // KYC permanent failure - user must contact support
  if (step === "kyc_permanent_failure") {
    return (
      <>
        <ModalBackButton onClick={handleBack} />
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <X className="h-8 w-8 text-red-600" />
          </div>
          <p className="mt-4 text-lg font-medium text-[--color-text-primary] text-center">
            Verification Rejected
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            {error ||
              "Your identity verification has been permanently rejected after multiple attempts."}
          </p>
          <p className="mt-4 text-sm text-[--color-text-secondary] text-center max-w-sm">
            Please contact support for assistance with your verification.
          </p>

          <div className="mt-6 w-full">
            <PrimaryButton onClick={onBack}>
              Choose Another Payment Method
            </PrimaryButton>
          </div>
        </div>
      </>
    );
  }

  // KYC popup step - opens verification in a new window
  if (step === "kyc_iframe") {
    // Before user clicks "Open Verification"
    if (!hasOpenedKycPopup) {
      return (
        <>
          <ModalBackButton onClick={handleBack} />
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
              <MousePointerClick className="h-8 w-8 text-blue-600" />
            </div>
            <p className="mt-4 text-lg font-medium text-[--color-text-primary] text-center">
              Verify your identity
            </p>
            <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
              Complete a quick verification with our partner Onramp. A new
              window will open where you can securely verify your identity.
            </p>

            <div className="mt-6 w-full">
              <PrimaryButton onClick={openKycPopup}>
                Open Verification
              </PrimaryButton>
            </div>
          </div>
        </>
      );
    }

    // After user has opened the popup - show waiting state
    return (
      <>
        <ModalBackButton onClick={handleBack} />
        <div className="flex flex-col items-center justify-center py-8">
          <LoaderCircle className="h-12 w-12 text-blue-500 animate-spin" />
          <p className="mt-4 text-lg font-medium text-[--color-text-primary] text-center">
            Waiting for verification
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            Please complete your identity verification in the popup window. This
            page will update automatically once you're done.
          </p>

          <div className="mt-6 w-full space-y-2">
            <PrimaryButton onClick={openKycPopup}>
              Reopen Verification Window
            </PrimaryButton>
            <button
              onClick={handleKycComplete}
              className="w-full px-4 py-2 text-sm font-medium text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors"
            >
              I've completed verification
            </button>
          </div>
        </div>
      </>
    );
  }

  // Creating transaction step
  if (step === "creating_transaction") {
    return (
      <>
        <ModalBackButton onClick={handleBack} />
        <div className="flex flex-col items-center justify-center py-12">
          <LoaderCircle className="h-12 w-12 text-blue-500 animate-spin" />
          <p className="mt-4 text-lg font-medium text-[--color-text-primary]">
            Creating your transaction...
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            Please wait while we set up your payment details.
          </p>
        </div>
      </>
    );
  }

  // QR code display and polling step
  if (step === "show_qr_code" || step === "polling_payment") {
    // Mobile: Show waiting screen with optional QR fallback
    if (isMobile && !showQrCode) {
      return (
        <>
          <ModalBackButton onClick={handleBack} />
          <div>
            <TransactionCheckoutHeader
              title="Complete payment"
              subtitle="Your UPI app should have opened"
              centeredSubtitle
            />

            <div className="mt-6 flex flex-col items-center">
              <LoaderCircle className="h-12 w-12 text-blue-500 animate-spin" />
              <p className="mt-4 text-lg font-medium text-[--color-text-primary]">
                Waiting for your payment...
              </p>
              <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
                Complete the payment in your UPI app. This screen will update
                automatically.
              </p>

              {/* UPI Details */}
              {upiDetails && (
                <div className="mt-6 w-full">
                  <div className="bg-[--color-background-secondary] p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[--color-text-secondary]">
                        Amount
                      </span>
                      <span className="text-sm font-medium text-[--color-text-primary]">
                        ₹{Number(upiDetails.amount).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-[--color-text-secondary]">
                        Transaction Note
                      </span>
                      <button
                        onClick={() => copyOtpToClipboard(upiDetails.otp)}
                        className="text-sm font-medium text-blue-500 hover:text-blue-600"
                      >
                        {isOtpCopied ? "Copied!" : upiDetails.otp}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Display UPI notes from the API response */}
              {upiNotes.length > 0 && (
                <div className="mt-4 w-full bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {upiNotes.map((note, index) => (
                    <p
                      key={index}
                      className="text-sm text-amber-800 text-center"
                    >
                      {note.msg}
                    </p>
                  ))}
                </div>
              )}

              {/* Fallback options - shown after 3 seconds */}
              {showQrFallbackButton && (
                <div className="mt-6 w-full space-y-2">
                  <PrimaryButton
                    onClick={() => (window.location.href = upiLink)}
                  >
                    Open UPI App Again
                  </PrimaryButton>
                  <button
                    onClick={() => setShowQrCode(true)}
                    className="w-full px-4 py-2 text-sm font-medium text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors"
                  >
                    Show QR Code instead
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      );
    }

    // Desktop or QR fallback view
    return (
      <>
        <ModalBackButton onClick={handleBack} />
        <div>
          <TransactionCheckoutHeader
            title="Scan to pay"
            subtitle="Use any UPI app to complete your payment"
            centeredSubtitle
          />

          <div className="mt-4 flex flex-col items-center">
            {/* QR Code */}
            <div className="bg-white p-4 rounded-lg border-2 border-[--color-modal-border]">
              <QRCode value={upiLink} size={180} />
            </div>

            {/* Mobile: Show option to open UPI app */}
            {isMobile && showQrCode && (
              <button
                onClick={() => (window.location.href = upiLink)}
                className="mt-3 px-4 py-2 text-sm font-medium text-blue-500 hover:text-blue-600 transition-colors"
              >
                Or open UPI app directly
              </button>
            )}

            {/* UPI Details */}
            {upiDetails && (
              <div className="mt-4 w-full space-y-3">
                <div className="bg-[--color-background-secondary] p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[--color-text-secondary]">
                      Pay to
                    </span>
                    <span className="text-sm font-medium text-[--color-text-primary]">
                      {upiDetails.vpa}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-[--color-text-secondary]">
                      Amount
                    </span>
                    <span className="text-sm font-medium text-[--color-text-primary]">
                      ₹{Number(upiDetails.amount).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-[--color-text-secondary]">
                      Transaction Note
                    </span>
                    <button
                      onClick={() => copyOtpToClipboard(upiDetails.otp)}
                      className="text-sm font-medium text-blue-500 hover:text-blue-600"
                    >
                      {isOtpCopied ? "Copied!" : upiDetails.otp}
                    </button>
                  </div>
                </div>

                {/* Waiting for payment indicator */}
                {step === "polling_payment" && (
                  <div className="flex items-center justify-center gap-2 py-2 px-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <LoaderCircle className="h-4 w-4 text-blue-500 animate-spin" />
                    <span className="text-sm text-blue-700">
                      Waiting for payment confirmation...
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-4">
            {/* Display UPI notes from the API response */}
            {upiNotes.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                {upiNotes.map((note, index) => (
                  <p key={index} className="text-sm text-amber-800 text-center">
                    {note.msg}
                  </p>
                ))}
              </div>
            )}

            <Alert
              id="onramp_qr_instructions"
              icon={
                <MousePointerClick strokeWidth={2.25} className="w-4 h-4" />
              }
              title="Complete payment with UPI"
              subtitle="Scan the QR code with any UPI app (Google Pay, PhonePe, Paytm, etc.) to complete your payment. Make sure to include the transaction note."
            />
          </div>
        </div>
      </>
    );
  }

  // Transaction error step
  if (step === "transaction_error") {
    return (
      <>
        <ModalBackButton onClick={handleBack} />
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <X className="h-8 w-8 text-red-600" />
          </div>
          <p className="mt-4 text-lg font-medium text-[--color-text-primary] text-center">
            Transaction Error
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            {error || "Unable to process transaction. Please try again."}
          </p>

          <div className="mt-4 w-full space-y-2">
            <PrimaryButton onClick={() => onContinue()}>
              Try Again
            </PrimaryButton>
            <button
              onClick={handleBack}
              className="w-full px-4 py-2 text-sm font-medium text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </>
    );
  }

  // Payment success step
  if (step === "payment_success") {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <CircleCheckBig className="h-8 w-8 text-green-600" />
          </div>
          <p className="mt-4 text-lg font-medium text-[--color-text-primary] text-center">
            Payment Successful!
          </p>
          <p className="mt-2 text-sm text-[--color-text-secondary] text-center max-w-sm">
            Your payment has been received and is being processed.
          </p>
          {cryptoAmount && (
            <div className="mt-4 text-center">
              <p className="text-sm text-[--color-text-secondary]">
                You'll receive
              </p>
              <p className="text-2xl font-medium text-[--color-text-primary] mt-1">
                {cryptoAmount} {session.sponsoredTransactionCurrencySymbol}
              </p>
            </div>
          )}
        </div>
      </>
    );
  }

  // Fallback - should not reach here
  return (
    <>
      <ModalBackButton onClick={onBack} />
      <div className="flex flex-col items-center justify-center py-8">
        <p className="text-[--color-text-secondary]">Loading...</p>
      </div>
    </>
  );
}
